// ── BCH Live — Player ─────────────────────────────────────────────
//
// Two engines behind one set of chrome:
//
//   • EmbedFrame — iframe for Twitch/YouTube/Kick/Rumble. Created
//     imperatively so a parent re-render can never restart playback.
//   • HlsVideo   — hls.js for .m3u8 origins, with the full recovery
//     ladder (retry → rebuild → CORS detection) carried over intact
//     from the original implementation, plus quality-level reporting.
//
// Chrome adds: theater mode, fullscreen, picture-in-picture, quality
// selector, keyboard shortcuts, mobile-friendly controls, reconnect
// affordance and proper loading states.
//
// NOTE ON PROVIDER EMBEDS: cross-origin iframes expose no playback API,
// so volume/quality/PiP cannot be driven from here for Twitch/YouTube
// etc. Those controls are shown only for the HLS engine, where they
// genuinely work, rather than rendering dead buttons.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Radio, VideoOff, ExternalLink, Maximize2, Minimize2, PictureInPicture2,
  Settings, Volume2, VolumeX, RefreshCw, Loader2, Play, Check, Keyboard, Activity,
} from 'lucide-react';
import { slog, swarn, type PlatformInfo } from '../../lib/streamCredentials';
import StreamDiagnostics from './StreamDiagnostics';

// ══════════════════════════════════════════════════════════════════
//  EMBED FRAME (provider iframes)
// ══════════════════════════════════════════════════════════════════

export const EmbedFrame = React.memo(function EmbedFrame({
  embedUrl, platform,
}: { embedUrl: string; platform: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const currentSrcRef = useRef<string>('');

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !embedUrl) return;

    if (!iframeRef.current) {
      slog('player', `creating iframe (${platform}) → ${embedUrl}`);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
      iframe.allowFullscreen = true;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.referrerPolicy = 'origin';
      iframe.onload = () => slog('player', 'iframe loaded');
      iframe.onerror = () => swarn('player', 'iframe failed to load');
      iframe.src = embedUrl;
      currentSrcRef.current = embedUrl;
      container.appendChild(iframe);
      iframeRef.current = iframe;
      return;
    }

    if (currentSrcRef.current !== embedUrl) {
      swarn('player', `stream URL changed — reloading player. old=${currentSrcRef.current} new=${embedUrl}`);
      currentSrcRef.current = embedUrl;
      iframeRef.current.src = embedUrl;
    }
  }, [embedUrl, platform]);

  useEffect(() => () => {
    const iframe = iframeRef.current;
    if (iframe) {
      slog('player', 'unmounting — destroying iframe');
      try { iframe.src = 'about:blank'; } catch { /* ignore */ }
      try { iframe.remove(); } catch { /* ignore */ }
    }
    iframeRef.current = null;
    currentSrcRef.current = '';
  }, []);

  return <div ref={containerRef} className="absolute inset-0 h-full w-full" />;
}, (prev, next) => prev.embedUrl === next.embedUrl && prev.platform === next.platform);

// ══════════════════════════════════════════════════════════════════
//  HLS ENGINE
// ══════════════════════════════════════════════════════════════════

export interface QualityLevel {
  index: number;      // -1 = auto
  label: string;
  height: number;
}

// ── Live diagnostics ──────────────────────────────────────────────
//
// A snapshot of what the HLS engine is actually experiencing, surfaced
// to the viewer-facing diagnostics panel so a dropping stream can be
// attributed to the right party (the host's server vs the app/relays).

export interface HlsDiagnostics {
  /** Engine in use, or null when the source isn't HLS. */
  engine: 'hls.js' | 'native' | null;
  /** Current playback state as the engine sees it. */
  state: 'loading' | 'playing' | 'stalled' | 'blocked';
  /** Wall-clock ms when the last media fragment loaded successfully. 0 = never. */
  lastFragOkAt: number;
  /** Last error detail string from hls.js, if any. */
  lastError: string | null;
  /** HTTP status of the last errored request (0 = network/CORS, undefined = none). */
  lastErrorHttp: number | undefined;
  /** True once the engine concluded the host is blocking cross-origin playback. */
  corsBlocked: boolean;
  /** Segment-load retry attempts since the last healthy stretch. */
  retryCount: number;
  /** Full engine rebuilds this session. */
  rebuildCount: number;
}

export const EMPTY_HLS_DIAGNOSTICS: HlsDiagnostics = {
  engine: null,
  state: 'loading',
  lastFragOkAt: 0,
  lastError: null,
  lastErrorHttp: undefined,
  corsBlocked: false,
  retryCount: 0,
  rebuildCount: 0,
};

interface HlsVideoProps {
  url: string;
  muted: boolean;
  onLevels?: (levels: QualityLevel[]) => void;
  /** -1 = auto */
  level?: number;
  onStateChange?: (s: 'loading' | 'playing' | 'stalled' | 'blocked') => void;
  /** Receives a full diagnostics snapshot whenever the engine's health changes. */
  onDiagnostics?: (d: HlsDiagnostics) => void;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  reloadKey?: number;
}

export const HlsVideo = React.memo(function HlsVideo({
  url, muted, onLevels, level = -1, onStateChange, onDiagnostics, videoRef, reloadKey = 0,
}: HlsVideoProps) {
  const localRef = useRef<HTMLVideoElement>(null);
  const ref = videoRef || localRef;
  const hlsRef = useRef<any>(null);
  const HlsClassRef = useRef<any>(null);
  const retryCountRef = useRef(0);
  const rebuildCountRef = useRef(0);
  const lastNonFatalLogRef = useRef(0);
  const lastFragOkRef = useRef(0);
  const corsCountRef = useRef(0);
  const [blocked, setBlocked] = useState<'cors' | null>(null);
  const genRef = useRef(0);
  const nativeCleanupRef = useRef<(() => void) | null>(null);
  const MAX_RETRIES = 20;
  const MAX_REBUILDS = 50;

  // Keep callbacks in refs so they never retrigger the engine effect.
  const onLevelsRef = useRef(onLevels);
  onLevelsRef.current = onLevels;
  const onStateRef = useRef(onStateChange);
  onStateRef.current = onStateChange;
  const onDiagRef = useRef(onDiagnostics);
  onDiagRef.current = onDiagnostics;

  // Latest diagnostics fields, mutated in place by the engine and flushed
  // through `emitDiag()`. Kept in a ref so updating it never re-runs the
  // engine effect.
  const diagRef = useRef<HlsDiagnostics>({ ...EMPTY_HLS_DIAGNOSTICS });
  const emitDiag = useCallback((patch: Partial<HlsDiagnostics>) => {
    diagRef.current = { ...diagRef.current, ...patch };
    onDiagRef.current?.(diagRef.current);
  }, []);

  // Report state transitions to the diagnostics panel as well as the caller.
  const reportState = useCallback((s: 'loading' | 'playing' | 'stalled' | 'blocked') => {
    onStateRef.current?.(s);
    emitDiag({ state: s });
  }, [emitDiag]);

  useEffect(() => {
    const v = ref.current; if (!v || !url) return; let destroyed = false;
    setBlocked(null);
    // Fresh session: reset the diagnostics snapshot.
    diagRef.current = { ...EMPTY_HLS_DIAGNOSTICS };
    reportState('loading');

    const playSafely = (video: HTMLVideoElement, gen: number) => {
      video.play().then(() => {
        if (gen === genRef.current) {
          slog('hls', 'playback started');
          reportState('playing');
        }
      }).catch((err: unknown) => {
        if (gen !== genRef.current || destroyed) return;
        const name = (err as { name?: string })?.name;
        if (name === 'AbortError') {
          swarn('hls', 'play() aborted — player was torn down mid-start (harmless if playback continues)');
        } else if (name === 'NotAllowedError') {
          swarn('hls', 'autoplay BLOCKED by browser policy — user must click play');
        } else {
          swarn('hls', `play() failed: ${name || 'unknown'}`, err);
        }
      });
    };

    const initHls = async () => {
      const myGen = ++genRef.current;
      if (!HlsClassRef.current) {
        try {
          HlsClassRef.current = (await import(/* @vite-ignore */ 'https://esm.sh/hls.js@1.5.17?target=esnext')).default;
        } catch (err) {
          swarn('hls', 'failed to load hls.js from CDN — retrying in 5s', err);
          if (!destroyed && genRef.current === myGen) setTimeout(initHls, 5000);
          return;
        }
      }
      if (destroyed || genRef.current !== myGen) return;
      const H = HlsClassRef.current;

      if (H.isSupported()) {
        if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } hlsRef.current = null; }

        const h = new H({
          enableWorker: false,
          // lowLatencyMode makes hls.js chase the live edge and request
          // segments the origin hasn't finished writing yet, producing endless
          // fragLoadError churn on many self-hosted origins. Reliability beats
          // shaving 2s of latency.
          lowLatencyMode: false,
          liveDurationInfinity: true,
          manifestLoadingMaxRetry: 10,
          manifestLoadingRetryDelay: 1500,
          manifestLoadingMaxRetryTimeout: 30000,
          levelLoadingMaxRetry: 10,
          levelLoadingRetryDelay: 1500,
          levelLoadingMaxRetryTimeout: 30000,
          fragLoadingMaxRetry: 10,
          fragLoadingRetryDelay: 1500,
          fragLoadingMaxRetryTimeout: 30000,
          maxBufferLength: 60,
          maxMaxBufferLength: 120,
          // Sit a few segments behind the live edge — playing too close is the
          // main cause of repeated fragment-load failures.
          liveSyncDurationCount: 4,
          liveMaxLatencyDurationCount: 20,
          backBufferLength: 30,
        });
        hlsRef.current = h;
        emitDiag({ engine: 'hls.js' });
        h.loadSource(url);
        h.attachMedia(v);
        slog('hls', `attaching hls.js player (gen ${myGen}) → ${url}`);

        h.on(H.Events.MANIFEST_PARSED, (_e: unknown, data: { levels?: Array<{ height?: number; bitrate?: number }> }) => {
          if (destroyed || genRef.current !== myGen || hlsRef.current !== h) {
            slog('hls', `manifest parsed but gen ${myGen} is stale (current ${genRef.current}) — not playing`);
            return;
          }
          slog('hls', 'manifest parsed — starting playback');

          // Report selectable quality levels to the chrome.
          const levels = (data?.levels || []).map((l, i) => ({
            index: i,
            height: l.height || 0,
            label: l.height ? `${l.height}p` : l.bitrate ? `${Math.round(l.bitrate / 1000)}kbps` : `Level ${i + 1}`,
          }));
          if (levels.length > 1) {
            onLevelsRef.current?.([{ index: -1, label: 'Auto', height: 0 }, ...levels.reverse()]);
          } else {
            onLevelsRef.current?.([]);
          }

          playSafely(v, myGen);
        });

        h.on(H.Events.ERROR, (_event: unknown, data: {
          fatal: boolean; type: string; details: string;
          response?: { code?: number }; url?: string;
        }) => {
          if (destroyed || genRef.current !== myGen || hlsRef.current !== h) return;

          if (!data.fatal) {
            const now = Date.now();
            if (now - lastNonFatalLogRef.current > 5000) {
              lastNonFatalLogRef.current = now;
              slog('hls', `non-fatal ${data.details} (${data.type})${data.response ? ` http=${data.response.code}` : ''} — hls.js is handling it`);
            }
            return;
          }

          const http = data.response?.code ? ` http=${data.response.code}` : '';
          const detail = `${data.details}${http}${data.url ? ` url=${String(data.url).slice(0, 120)}` : ''}`;

          // Record the fatal error for the diagnostics panel.
          emitDiag({ lastError: data.details, lastErrorHttp: data.response?.code });

          // hls.js fetches segments with XHR, so a missing
          // Access-Control-Allow-Origin surfaces as a network error with HTTP
          // status 0. No amount of retrying fixes a server misconfiguration.
          const looksBlocked = data.type === H.ErrorTypes.NETWORK_ERROR
            && (data.response?.code === 0 || data.response?.code === undefined)
            && ['fragLoadError', 'levelLoadError', 'manifestLoadError', 'audioTrackLoadError'].includes(data.details);

          if (looksBlocked) {
            corsCountRef.current++;
            if (corsCountRef.current >= 3) {
              swarn('hls', `giving up: the stream host is not sending CORS headers, so the browser is blocking playback. This must be fixed on the host (${detail})`);
              try { h.destroy(); } catch { /* ignore */ }
              hlsRef.current = null;
              setBlocked('cors');
              emitDiag({ corsBlocked: true });
              reportState('blocked');
              return;
            }
          }

          switch (data.type) {
            case H.ErrorTypes.NETWORK_ERROR:
              reportState('stalled');
              if (retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current++;
                emitDiag({ retryCount: retryCountRef.current });
                const delay = Math.min(1000 * retryCountRef.current, 8000);
                swarn('hls', `network error: ${detail} — recovering in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
                setTimeout(() => { if (!destroyed && hlsRef.current === h) h.startLoad(); }, delay);
              } else {
                retryCountRef.current = 0;
                rebuildCountRef.current++;
                emitDiag({ retryCount: 0, rebuildCount: rebuildCountRef.current });
                if (rebuildCountRef.current < MAX_REBUILDS) {
                  swarn('hls', `rebuilding player (rebuild ${rebuildCountRef.current})`);
                  try { h.destroy(); } catch { /* ignore */ } hlsRef.current = null;
                  setTimeout(() => { if (!destroyed && genRef.current === myGen) initHls(); }, 3000);
                }
              }
              break;
            case H.ErrorTypes.MEDIA_ERROR:
              swarn('hls', `media error: ${detail} — recovering`);
              h.recoverMediaError();
              break;
            default:
              rebuildCountRef.current++;
              emitDiag({ rebuildCount: rebuildCountRef.current });
              if (rebuildCountRef.current < MAX_REBUILDS) {
                swarn('hls', `fatal ${data.type}: ${detail} — rebuilding (rebuild ${rebuildCountRef.current})`);
                try { h.destroy(); } catch { /* ignore */ } hlsRef.current = null;
                setTimeout(() => { if (!destroyed && genRef.current === myGen) initHls(); }, 5000);
              }
              break;
          }
        });

        // Only clear the retry counter after playback has been genuinely
        // healthy for a while. Clearing on every fragment let a stream that
        // loaded one segment then failed ping-pong at "attempt 1" forever.
        h.on(H.Events.FRAG_LOADED, () => {
          const now = Date.now();
          if (lastFragOkRef.current && now - lastFragOkRef.current > 15_000 && retryCountRef.current > 0) {
            slog('hls', `playback healthy for 15s — clearing retry counter (was ${retryCountRef.current})`);
            retryCountRef.current = 0;
            rebuildCountRef.current = 0;
          }
          corsCountRef.current = 0;
          if (!lastFragOkRef.current) lastFragOkRef.current = now;
          emitDiag({ lastFragOkAt: now, retryCount: retryCountRef.current });
          reportState('playing');
        });

        let lastPlayPos = 0;
        let stallCount = 0;
        const healthCheck = setInterval(() => {
          if (destroyed || !v || v.paused) return;
          if (v.currentTime === lastPlayPos && v.readyState >= 2) {
            stallCount++;
            if (stallCount >= 3 && hlsRef.current === h) {
              swarn('hls', 'playback stalled — attempting recovery');
              stallCount = 0;
              reportState('stalled');
              h.startLoad();
            }
          } else {
            stallCount = 0;
          }
          lastPlayPos = v.currentTime;
        }, 5000);

        const origDestroy = h.destroy.bind(h);
        h.destroy = () => { clearInterval(healthCheck); origDestroy(); };

      } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS. Listeners attached once and tracked, so a long
        // stream doesn't accumulate duplicate retry handlers.
        slog('hls', `using Safari native HLS → ${url}`);
        onLevelsRef.current?.([]);
        emitDiag({ engine: 'native' });
        const onMeta = () => {
          if (genRef.current !== myGen) return;
          playSafely(v, myGen);
        };
        const onErr = () => {
          if (destroyed || genRef.current !== myGen) return;
          reportState('stalled');
          if (retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current++;
            emitDiag({ retryCount: retryCountRef.current, lastError: `native code ${v.error?.code ?? '?'}` });
            const delay = Math.min(2000 * retryCountRef.current, 10000);
            swarn('hls', `native HLS error (code ${v.error?.code ?? '?'}) — retrying in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
            setTimeout(() => {
              if (destroyed || genRef.current !== myGen) return;
              v.src = url; v.load(); playSafely(v, myGen);
            }, delay);
          } else {
            swarn('hls', 'native HLS gave up after max retries');
          }
        };
        v.addEventListener('loadedmetadata', onMeta);
        v.addEventListener('error', onErr);
        nativeCleanupRef.current = () => {
          v.removeEventListener('loadedmetadata', onMeta);
          v.removeEventListener('error', onErr);
        };
        v.src = url;
      } else {
        swarn('hls', 'no HLS support in this browser — cannot play this stream');
      }
    };

    initHls();
    return () => {
      destroyed = true;
      genRef.current++;
      if (nativeCleanupRef.current) { nativeCleanupRef.current(); nativeCleanupRef.current = null; }
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } hlsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, reloadKey]);

  // Apply quality selection without rebuilding the engine.
  useEffect(() => {
    const h = hlsRef.current;
    if (!h) return;
    try {
      h.currentLevel = level;
      slog('hls', `quality set to ${level === -1 ? 'auto' : `level ${level}`}`);
    } catch { /* engine not ready */ }
  }, [level]);

  // Keep the muted attribute in sync with chrome state.
  useEffect(() => {
    const v = ref.current;
    if (v) v.muted = muted;
  }, [muted, ref]);

  if (blocked === 'cors') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 bg-black px-6 text-center text-white">
        <VideoOff className="h-10 w-10 text-red-500/70" />
        <p className="text-sm font-semibold">This stream can’t be embedded</p>
        <p className="max-w-sm text-xs text-white/60">
          The streaming server isn’t allowing playback from other sites, so your
          browser is blocking the video. This has to be fixed by the stream host.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
        >
          <ExternalLink className="h-4 w-4" /> Open stream directly
        </a>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      className="absolute inset-0 h-full w-full bg-black"
      playsInline
      autoPlay
      muted={muted}
    />
  );
});

// ══════════════════════════════════════════════════════════════════
//  PLAYER CHROME
// ══════════════════════════════════════════════════════════════════

export interface LivePlayerProps {
  embedUrl: string | null;
  platform: string;
  originalUrl: string;
  isLive: boolean;
  title?: string;
  /** Rendered over the video (tip overlays) */
  overlay?: React.ReactNode;
  theater: boolean;
  onTheaterChange: (v: boolean) => void;
  /** Diagnostics context — enables the viewer-facing "why is it dropping" panel. */
  diagnostics?: {
    platformInfo: PlatformInfo;
    heartbeatAge: number;
    connection: { connectedCount: number; total: number; status: 'good' | 'degraded' | 'poor' };
    isOwner: boolean;
  };
}

export default function LivePlayer({
  embedUrl, platform, originalUrl, isLive, title,
  overlay, theater, onTheaterChange, diagnostics,
}: LivePlayerProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isHls = !embedUrl && platform === 'hls';
  const hasPlayer = !!embedUrl || isHls;

  const [muted, setMuted] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [levels, setLevels] = useState<QualityLevel[]>([]);
  const [level, setLevel] = useState(-1);
  const [showSettings, setShowSettings] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [state, setState] = useState<'loading' | 'playing' | 'stalled' | 'blocked'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimer = useRef<number | null>(null);
  const [hlsDiag, setHlsDiag] = useState<HlsDiagnostics>(EMPTY_HLS_DIAGNOSTICS);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // ── Fullscreen ──
  const toggleFullscreen = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Picture-in-Picture (HLS only — no API for cross-origin iframes) ──
  const togglePiP = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await v.requestPictureInPicture();
    } catch (err) {
      swarn('player', 'picture-in-picture unavailable', err);
    }
  }, []);

  const pipSupported = isHls
    && typeof document !== 'undefined'
    && 'pictureInPictureEnabled' in document
    && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled === true;

  // ── Auto-hide chrome while playing ──
  const nudgeChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setChromeVisible(false), 2800);
  }, []);

  // Unmute from a user gesture. Setting muted=false alone can leave iOS
  // Safari paused, so we also nudge play() — and because this runs inside a
  // real tap, the browser honours the audio.
  const unmute = useCallback(() => {
    setMuted(false);
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => { /* autoplay policy will surface elsewhere */ });
    }
    nudgeChrome();
  }, [nudgeChrome]);

  // Tap the video (touch) to toggle the control bar. On desktop the chrome
  // follows the pointer; on touch there is no hover, so a bare tap must
  // reveal the controls — otherwise the mute button is unreachable once the
  // bar auto-hides. Tapping again while visible hides it.
  const toggleChrome = useCallback(() => {
    setChromeVisible(prev => {
      if (prev) {
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
        return false;
      }
      nudgeChrome();
      return true;
    });
  }, [nudgeChrome]);

  useEffect(() => {
    nudgeChrome();
    return () => { if (hideTimer.current) window.clearTimeout(hideTimer.current); };
  }, [nudgeChrome]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never hijack typing in the chat composer or any input.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key.toLowerCase()) {
        case 'f': e.preventDefault(); toggleFullscreen(); break;
        case 't': e.preventDefault(); onTheaterChange(!theater); break;
        case 'm': e.preventDefault(); if (muted) { unmute(); } else { setMuted(true); nudgeChrome(); } break;
        case 'i': if (pipSupported) { e.preventDefault(); togglePiP(); } break;
        case '?': e.preventDefault(); setShowShortcuts(s => !s); break;
        case 'escape':
          if (theater && !document.fullscreenElement) { e.preventDefault(); onTheaterChange(false); }
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFullscreen, onTheaterChange, theater, pipSupported, togglePiP, nudgeChrome, muted, unmute]);

  const reconnect = useCallback(() => {
    slog('player', 'manual reconnect requested');
    setState('loading');
    setReloadKey(k => k + 1);
  }, []);

  const qualityLabel = useMemo(() => {
    if (level === -1) return 'Auto';
    return levels.find(l => l.index === level)?.label || 'Auto';
  }, [level, levels]);

  // ── Offline / no-URL states ──
  if (!hasPlayer) {
    return (
      <PlayerShell ref={shellRef} theater={theater}>
        <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 bg-black text-center text-white">
          {isLive ? (
            <>
              <Radio className="h-10 w-10 text-red-500" />
              <p className="text-sm font-semibold">This stream can’t be embedded here</p>
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
              >
                <ExternalLink className="h-4 w-4" /> Open stream
              </a>
            </>
          ) : (
            <>
              <VideoOff className="h-10 w-10 text-white/30" />
              <p className="text-sm font-semibold text-white/70">Stream offline</p>
            </>
          )}
        </div>
      </PlayerShell>
    );
  }

  return (
    <PlayerShell
      ref={shellRef}
      theater={theater}
      onPointerMove={nudgeChrome}
      onPointerLeave={() => setChromeVisible(false)}
    >
      {embedUrl ? (
        <EmbedFrame key={platform} embedUrl={embedUrl} platform={platform} />
      ) : (
        <HlsVideo
          url={originalUrl}
          muted={muted}
          onLevels={setLevels}
          level={level}
          onStateChange={setState}
          onDiagnostics={setHlsDiag}
          videoRef={videoRef}
          reloadKey={reloadKey}
        />
      )}

      {/* Touch tap-zone: reveals/hides the control bar on tap. HLS only —
          provider iframes own their own tap surface. Sits below the unmute
          overlay (z-20) and control bar (z-30) so those stay tappable. */}
      {isHls && (
        <button
          type="button"
          aria-label={chromeVisible ? 'Hide controls' : 'Show controls'}
          tabIndex={-1}
          onClick={toggleChrome}
          className="absolute inset-0 z-10 cursor-default"
        />
      )}

      {/* Loading / stalled states. Never covers provider embeds, which
          render their own spinners and would otherwise be double-masked. */}
      {isHls && state === 'loading' && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-xs font-medium text-white/70">Connecting to stream…</p>
        </div>
      )}
      {isHls && state === 'stalled' && (
        <div className="absolute inset-x-0 top-3 z-20 flex justify-center">
          <span className="flex items-center gap-2 rounded-full bg-black/80 px-3 py-1.5 text-[11px] font-semibold text-amber-300 backdrop-blur-sm">
            <Loader2 className="h-3 w-3 animate-spin" /> Reconnecting…
          </span>
        </div>
      )}

      {/* Tip overlays */}
      {overlay}

      {/* Tap-to-unmute — HLS autoplays muted (browser policy), so surface a
          big, obvious unmute target instead of making viewers hunt for the
          small icon in the auto-hiding control bar. This is the #1 mobile
          pain point: the tiny mute button is cramped among other controls
          that vanish after a couple of seconds. A large centered button is
          the standard fix. Shown only while HLS is actually playing muted. */}
      {isHls && muted && state === 'playing' && (
        <button
          type="button"
          onClick={unmute}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/10"
          aria-label="Tap to unmute"
        >
          <span className="flex items-center gap-2 rounded-full bg-black/75 px-5 py-3 text-sm font-bold text-white shadow-2xl backdrop-blur-sm ring-1 ring-white/20 active:scale-95 transition-transform">
            <VolumeX className="h-5 w-5" />
            Tap to unmute
          </span>
        </button>
      )}

      {/* Control bar.
          NOTE: when hidden it MUST be pointer-events-none. An `opacity-0`
          element still captures taps, and this bar spans the lower third of
          the player at z-30 — while invisible it was silently swallowing taps
          meant for provider embeds underneath (e.g. Twitch's own
          "Start Watching" mature-content button), so tapping to start a
          Twitch stream on mobile did nothing. */}
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-center gap-1 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-2 pb-2 pt-8 transition-opacity duration-300 sm:px-3 [&_button]:pointer-events-auto ${
          chromeVisible || showSettings ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {isLive && (
          <span className="mr-1 flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
          </span>
        )}

        {title && (
          <span className="mr-auto hidden max-w-[38%] truncate text-xs font-semibold text-white/85 sm:block">
            {title}
          </span>
        )}
        {!title && <span className="mr-auto" />}

        {/* Mute — HLS only; iframes own their own audio */}
        {isHls && (
          <ChromeButton label={muted ? 'Unmute (m)' : 'Mute (m)'} onClick={() => { if (muted) { unmute(); } else { setMuted(true); nudgeChrome(); } }}>
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </ChromeButton>
        )}

        {isHls && (
          <ChromeButton label="Reconnect" onClick={reconnect}>
            <RefreshCw className="h-4 w-4" />
          </ChromeButton>
        )}

        {/* Quality — only when the manifest actually offers levels */}
        {isHls && levels.length > 1 && (
          <div className="relative">
            <ChromeButton
              label={`Quality: ${qualityLabel}`}
              onClick={() => setShowSettings(s => !s)}
              active={showSettings}
            >
              <Settings className="h-4 w-4" />
            </ChromeButton>
            {showSettings && (
              <div className="absolute bottom-full right-0 mb-2 min-w-[9rem] overflow-hidden rounded-xl border border-white/15 bg-black/95 py-1 shadow-2xl backdrop-blur-sm">
                <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/40">Quality</p>
                {levels.map(l => (
                  <button
                    key={l.index}
                    onClick={() => { setLevel(l.index); setShowSettings(false); }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-white/85 hover:bg-white/10"
                  >
                    {l.label}
                    {level === l.index && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {pipSupported && (
          <ChromeButton label="Picture-in-picture (i)" onClick={togglePiP}>
            <PictureInPicture2 className="h-4 w-4" />
          </ChromeButton>
        )}

        {diagnostics && (
          <ChromeButton
            label="Stream diagnostics — why is it dropping?"
            onClick={() => setShowDiagnostics(true)}
            active={showDiagnostics}
          >
            <Activity className="h-4 w-4" />
          </ChromeButton>
        )}

        <ChromeButton label="Keyboard shortcuts (?)" onClick={() => setShowShortcuts(s => !s)}>
          <Keyboard className="h-4 w-4" />
        </ChromeButton>

        <ChromeButton
          label={theater ? 'Exit theater (t)' : 'Theater mode (t)'}
          onClick={() => onTheaterChange(!theater)}
          active={theater}
        >
          {theater ? <Minimize2 className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </ChromeButton>

        <ChromeButton label={fullscreen ? 'Exit fullscreen (f)' : 'Fullscreen (f)'} onClick={toggleFullscreen}>
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </ChromeButton>
      </div>

      {/* Shortcut help */}
      {showShortcuts && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowShortcuts(false)}>
          <div className="w-full max-w-xs rounded-2xl border border-white/15 bg-card p-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h4 className="mb-3 text-sm font-bold">Keyboard shortcuts</h4>
            <ul className="space-y-2 text-xs text-muted-foreground">
              {[
                ['F', 'Fullscreen'],
                ['T', 'Theater mode'],
                ['M', 'Mute / unmute'],
                ...(pipSupported ? [['I', 'Picture-in-picture']] : []),
                ['?', 'Toggle this help'],
                ['Esc', 'Exit theater'],
              ].map(([k, d]) => (
                <li key={k} className="flex items-center justify-between gap-3">
                  <span>{d}</span>
                  <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-bold text-foreground">{k}</kbd>
                </li>
              ))}
            </ul>
            <button
              onClick={() => setShowShortcuts(false)}
              className="mt-4 w-full rounded-xl bg-primary py-2 text-xs font-bold text-primary-foreground"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Viewer-facing diagnostics */}
      {showDiagnostics && diagnostics && (
        <StreamDiagnostics
          input={{
            isLive,
            isHls,
            platform: diagnostics.platformInfo,
            heartbeatAge: diagnostics.heartbeatAge,
            hls: hlsDiag,
            connection: diagnostics.connection,
            isOwner: diagnostics.isOwner,
          }}
          onClose={() => setShowDiagnostics(false)}
        />
      )}
    </PlayerShell>
  );
}

// ── Shell: keeps 16:9 and handles theater framing ─────────────────
const PlayerShell = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    theater: boolean;
    onPointerMove?: () => void;
    onPointerLeave?: () => void;
  }
>(function PlayerShell({ children, theater, onPointerMove, onPointerLeave }, ref) {
  return (
    <div
      ref={ref}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      className={`group/player relative w-full overflow-hidden bg-black ${
        theater
          ? 'flex max-h-full items-center justify-center rounded-none'
          : 'rounded-xl border border-border shadow-lg sm:rounded-2xl'
      }`}
    >
      {/* Theater sizing uses container-relative units (aspect-ratio + max
          height) rather than vw, so the player stays correct whether it
          spans the viewport or shares the row with a docked chat panel. */}
      <div
        className="relative w-full"
        style={theater
          ? { aspectRatio: '16 / 9', maxHeight: '100%' }
          : { paddingBottom: '56.25%' }}
      >
        {children}
      </div>
    </div>
  );
});

// ── Chrome button ─────────────────────────────────────────────────
function ChromeButton({
  children, label, onClick, active,
}: { children: React.ReactNode; label: string; onClick: () => void; active?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`pointer-events-auto rounded-lg p-2 text-white/85 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 active:scale-95 ${
        active ? 'bg-white/20 text-white' : ''
      }`}
    >
      {children}
    </button>
  );
}
